// src/pages/CallTrackModule/searchHistory/search.js

import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import Navbar from "../../Navbar";

const SearchAsset = () => {
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);

  const { API_CONFIG } = require("../../../configuration");

  const token = localStorage.getItem("token");

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      alert("Enter Customer ID or Mobile Number");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setResults([]);

      const response = await axios.get(
        `${API_CONFIG.APIURL}/customer-search`,
        {
          params: { search: searchValue },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setResults(response.data.data || []);

      if (!response.data.data || response.data.data.length === 0) {
        setError("No matching records found");
      }
    } catch (err) {
      console.error(err);
      setError("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (row) => {
    try {
      setDetailsOpen(true);
      setDetailsLoading(true);
      setDetailsData(null);

      const response = await axios.post(
        `${API_CONFIG.APIURL}/customer-details`,
        {
          customer_id: row.customer_id,
          mobile_number1: row.mobile_number1,
          mobile_number2: row.mobile_number2,
          sheet_id: row.sheet_id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setDetailsData(response.data.data);
    } catch (err) {
      console.error(err);
      setDetailsData({ error: "Failed to load details" });
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          ml: { xs: 0, md: "250px" },
          width: { xs: "100%", md: "calc(100% - 250px)" },
          p: 3,
          background: "#f5f7fa",
          minHeight: "100vh",
        }}
      >
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight={700} mb={3}>
            Call Search
          </Typography>

          <Box display="flex" gap={2}>
            <TextField
              label="Customer ID / Mobile Number"
              fullWidth
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
          </Box>

          {loading && (
            <Box textAlign="center" mt={4}>
              <CircularProgress />
            </Box>
          )}

          {error && !loading && (
            <Typography color="error" mt={3}>
              {error}
            </Typography>
          )}

          {results.length > 0 && !loading && (
            <Box mt={4} sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sheet</TableCell>
                    <TableCell>Customer ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Mobile 1</TableCell>
                    <TableCell>Mobile 2</TableCell>
                    <TableCell>Tele-Caller</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Records</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row) => (
                    <TableRow key={`${row.customer_id}_${row.sheet_id}`}>
                      <TableCell>{row.sheet_name}</TableCell>
                      <TableCell>{row.customer_id}</TableCell>
                      <TableCell>{row.customer_name}</TableCell>
                      <TableCell>{row.branch}</TableCell>
                      <TableCell>{row.mobile_number1}</TableCell>
                      <TableCell>{row.mobile_number2}</TableCell>
                      <TableCell>{row.tele_caller}</TableCell>
                      <TableCell>{row.date_of_data}</TableCell>
                      <TableCell align="center">{row.upload_count}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewDetails(row)}
                        >
                          View History
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Customer Call History</DialogTitle>
        <DialogContent dividers>
          {detailsLoading && (
            <Box textAlign="center" p={3}>
              <CircularProgress />
            </Box>
          )}

          {detailsData?.error && (
            <Typography color="error">{detailsData.error}</Typography>
          )}

          {detailsData?.histories?.map((h, idx) => (
            <Box key={idx} mb={4}>
              <Typography fontWeight={700} mb={1}>
                {h.customer.customer_name} ({h.customer.customer_id}) —{" "}
                {h.customer.branch}
              </Typography>

              <Box sx={{ overflowX: "auto", mb: 2, border: "1px solid #ddd" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: "#1565c0",
                        "&:hover": { backgroundColor: "#1565c0" },
                      }}
                    >
                      <TableCell sx={{ color: "#fff" }}>Region</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Area</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Branch</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Customer ID</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Loan ID</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Customer Name</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Mobile 1</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Mobile 2</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Tele-Caller</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Date of Data</TableCell>
                      {h.questions.map((q) => (
                        <TableCell sx={{ color: "#fff" }} key={q.question_id}>
                          {q.question_text}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{h.customer.region}</TableCell>
                      <TableCell>{h.customer.area}</TableCell>
                      <TableCell>{h.customer.branch}</TableCell>
                      <TableCell>{h.customer.customer_id}</TableCell>
                      <TableCell>{h.customer.loan_id}</TableCell>
                      <TableCell>{h.customer.customer_name}</TableCell>
                      <TableCell>{h.customer.mobile_number1}</TableCell>
                      <TableCell>{h.customer.mobile_number2}</TableCell>
                      <TableCell>{h.customer.tele_caller}</TableCell>
                      <TableCell>{h.customer.date_of_data}</TableCell>
                      {h.questions.map((q) => (
                        <TableCell key={q.question_id}>
                          {q.answer || "-"}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography fontWeight={600} mt={1} mb={1}>
                Actions / Remarks
              </Typography>

              {h.actions.length === 0 && (
                <Typography variant="body2">No actions yet</Typography>
              )}

              {h.actions.length > 0 && (
                <Box sx={{ overflowX: "auto", border: "1px solid #ddd" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#1565c0",
                          "&:hover": { backgroundColor: "#1565c0" },
                        }}
                      >
                        <TableCell sx={{ color: "#fff" }}>By</TableCell>
                        <TableCell sx={{ color: "#fff" }}>Role</TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          Call Status
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          Case Status
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>Remarks</TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          Date/Time
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {h.actions.map((a) => {
                        const remarksText =
                          [a.remarks1, a.remarks2, a.remarks3]
                            .filter(Boolean)
                            .join(" | ") || "-";

                        return (
                          <TableRow key={a.id}>
                            <TableCell>{a.action_by}</TableCell>
                            <TableCell>{a.action_by_role || "-"}</TableCell>
                            <TableCell>{a.call_status || "-"}</TableCell>
                            <TableCell>{a.case_status || "-"}</TableCell>
                            <TableCell
                              sx={{
                                maxWidth: 220,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <Tooltip title={remarksText} arrow>
                                <span>{remarksText}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {new Date(a.created_at).toLocaleString("en-IN", {
                                timeZone: "Asia/Kolkata",
                              })}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SearchAsset;